/* [PATCH izdaja-ui v1] Aritmetika in oblikovanje zneskov modula Izdaja — zrcalo backend/app/services/izdaja/zneski.py
   (ROUND_HALF_UP na cent, DDV iz seštete osnove po (vrsta, stopnja)). Backend je vir resnice — po vsakem shranjevanju
   UI prevzame njegove zneske; ta koda služi ŽIVEMU prikazu med tipkanjem. Standard modula (faza 1): decimalka z vejico,
   tisočica s piko (de-DE, ker sl-SI pod 10.000 ne grupira), interne kode ASCII, prikaz s šumniki. */

export type PostavkaVnos = {
  kljuc: string;              // lokalni ključ vrstice (React key)
  id?: string | null;         // id postavke na strežniku (po shranjevanju)
  artikel_id: string | null;
  sifra: string; naziv: string; opis: string; vrsta: string;
  kolicina: string; enota: string; enota_koda: string;
  cena: string; popust_pct: string; ddv_stopnja: string; ddv_vrsta: string;
  konto_prihodki: string; sm: string; projekt: string; poracun_dok_id?: string | null; izvor_postavka_id?: string | null; /* [PATCH dobavnica-ponudba-fe v1b] */ // [PATCH izdaja-poracun-avansa v1]
  neto_teza?: string; bruto_teza?: string; carinska_tarifa?: string; drzava_porekla?: string; // [PATCH dobavnica-cmr-fe v1] kg skupaj za vrstico
};

export type RekapVrstica = { ddv_vrsta: string; stopnja: number; osnova: number; ddv: number };

/** '1.234,50' | '1234.50' | '45,5' | 45.5 -> število; prazno/neveljavno -> 0 (pika je tisočica SAMO skupaj z vejico). */
export function stevilo(v: string | number | null | undefined): number {
  if (v == null) return 0;
  if (typeof v === 'number') return isFinite(v) ? v : 0;
  let t = String(v).trim().replace(/\s/g, '');
  if (!t) return 0;
  if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.');
  const n = Number(t);
  return isFinite(n) ? n : 0;
}

/** ROUND_HALF_UP na 2 decimalki, simetrično za negativne (kot Decimal.quantize v backendu). */
export function r2(x: number): number {
  const s = x < 0 ? -1 : 1;
  return s * Math.round(Math.abs(x) * 100 + 1e-9) / 100;
}

/** Prikaz zneska: 1.234,50 (de-DE). Prazno za null. */
export function fmt(v: string | number | null | undefined, dec = 2): string {
  if (v == null || v === '') return '';
  const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
  if (!isFinite(n)) return '';
  return n.toLocaleString('de-DE', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

/** Za vnosna polja: '45.5' -> '45,5' (brez tisočic, brez dopolnjevanja ničel). */
export function stevSl(v: string | number | null | undefined): string {
  if (v == null || v === '') return '';
  const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
  if (!isFinite(n)) return String(v);
  return String(n).replace('.', ',');
}

/** Za pošiljanje na API: '45,5' -> '45.5'; prazno -> null. */
export function zaApi(v: string | number | null | undefined): string | null {
  if (v == null || v === '') return null;
  return String(stevilo(v));
}

export const STOPNJA_PO_VRSTI: Record<string, number> = {
  IZD_22: 22, IZD_95: 9.5, IZD_5: 5, IZD_OPROSCEN: 0, IZD_EU_OPROSCEN: 0, IZD_EU_STORITVE: 0,
  IZD_76A: 0, IZD_76A_95: 0, IZD_IZVOZ: 0,
};

/** Oznake vrst DDV (interne kode ASCII, prikaz s šumniki). */
export const VRSTA_DDV_OZNAKE: Record<string, string> = {
  IZD_22: 'DDV 22 %', IZD_95: 'DDV 9,5 %', IZD_5: 'DDV 5 %', IZD_OPROSCEN: 'oproščeno',
  IZD_EU_OPROSCEN: 'EU blago — 46. člen', IZD_EU_STORITVE: 'EU storitev — 25. člen',
  IZD_76A: '76.a člen (22 %)', IZD_76A_95: '76.a člen (9,5 %)', IZD_IZVOZ: 'izvoz — 52. člen',
  IZD_NI_PREDMET: 'ne gre v knjigo (ni predmet DDV)', // [PATCH izdaja-ni-predmet v1]
};

/** Izbirnik DDV na vrstici: uporabnik izbere stopnjo ali posebnost; režim kupca (EU/tretja) določi čuvaj na strežniku. */
/** [PATCH izdaja-predpisi v1] razlogi oprostitve (ZDDV-1 82. čl.) — kode kot v backend klavzule.OPROSTITVE */
export const OPROSTITVE: { v: string; l: string }[] = [
  { v: 'K42_ZDRAVSTVO', l: '42. člen — zdravstvene storitve' }, { v: 'K42_SOCIALNO', l: '42. člen — socialno varstvo' },
  { v: 'K42_IZOBRAZEVANJE', l: '42. člen — izobraževanje' }, { v: 'K42_SPORT', l: '42. člen — šport' }, { v: 'K42_KULTURA', l: '42. člen — kultura' },
  { v: 'K44_ZAVAROVANJE', l: '44. člen — zavarovanje' }, { v: 'K44_NAJEM', l: '44. člen — najem nepremičnin' },
  { v: 'K44_FINANCE', l: '44. člen — finančne storitve' }, { v: 'K44_NEPREMICNINE', l: '44. člen — dobava objektov/zemljišč' },
  { v: 'OPR_DRUGO', l: 'drugo — vpišem besedilo klavzule' },
];
/** [PATCH izdaja-ni-predmet v1] razlogi za »ne gre v knjigo — ni predmet DDV« — kode kot v backend klavzule.NI_PREDMET_RAZLOGI */
export const NI_PREDMET_RAZLOGI: { v: string; l: string }[] = [
  { v: 'NP_PREHODNA', l: 'prehodna postavka v imenu in za račun kupca (36. čl. 6. odst. 3. tč.) — npr. turistična taksa' },
  { v: 'NP_ODSKODNINA', l: 'odškodnina, zamudne obresti, pogodbena kazen (3. čl. ZDDV-1, 13. čl. PZDDV)' },
  { v: 'NP_DRUGO', l: 'drugo — vpišem besedilo klavzule' },
];
export const DDV_IZBIRE: { v: string; l: string; stopnja: string; vrsta: string; skupina?: string }[] = [ // [PATCH izdaja-ddv-odlocitev v1] vse možnosti, uporabnik odloča
  { v: '22', l: '22 %', stopnja: '22', vrsta: 'IZD_22', skupina: 'Domači promet' },  // [PATCH izdaja-ddv-odlocitev v1b] izrecna izbira
  { v: '9.5', l: '9,5 %', stopnja: '9.5', vrsta: 'IZD_95', skupina: 'Domači promet' },
  { v: '5', l: '5 %', stopnja: '5', vrsta: 'IZD_5', skupina: 'Domači promet' },
  { v: 'IZD_OPROSCEN', l: 'oproščeno (0 %)', stopnja: '0', vrsta: 'IZD_OPROSCEN', skupina: 'Domači promet' },
  { v: 'IZD_76A', l: '76.a člen (obrnjena, 22 %)', stopnja: '0', vrsta: 'IZD_76A', skupina: 'Domači promet' },
  { v: 'IZD_76A_95', l: '76.a člen (obrnjena, 9,5 %)', stopnja: '0', vrsta: 'IZD_76A_95', skupina: 'Domači promet' },
  { v: 'IZD_NI_PREDMET', l: 'ne gre v knjigo — ni predmet DDV (0 %)', stopnja: '0', vrsta: 'IZD_NI_PREDMET', skupina: 'Domači promet' }, // [PATCH izdaja-ni-predmet v1]
  { v: 'IZD_EU_STORITVE', l: 'EU storitev — 25. člen (obrnjena, 0 %)', stopnja: '0', vrsta: 'IZD_EU_STORITVE', skupina: 'Kupec v EU (z ID za DDV)' },
  { v: 'IZD_EU_OPROSCEN', l: 'EU blago — 46. člen (oproščeno, 0 %)', stopnja: '0', vrsta: 'IZD_EU_OPROSCEN', skupina: 'Kupec v EU (z ID za DDV)' },
  { v: 'IZD_IZVOZ', l: 'izvoz blaga — 52. člen (0 %)', stopnja: '0', vrsta: 'IZD_IZVOZ', skupina: 'Kupec izven EU' },
];

/** Vrednost izbirnika iz postavke (vrsta ima prednost; sicer stopnja normalizirana: '9.50' -> '9.5'). */
export function ddvIzbira(p: { ddv_stopnja: string; ddv_vrsta: string }): string {
  if (p.ddv_vrsta && DDV_IZBIRE.some(o => o.v === p.ddv_vrsta)) return p.ddv_vrsta;
  if (p.ddv_vrsta) return p.ddv_vrsta;                 // EU/izvoz vrsta, ki jo je določil čuvaj (prikaže se kot lastna opcija)
  return String(stevilo(p.ddv_stopnja));
}

export function izracunajPostavko(p: PostavkaVnos): { osnova: number; ddv: number; skupaj: number; stopnja: number } {
  const kolicina = stevilo(p.kolicina || '1');
  const cena = stevilo(p.cena);
  const popust = stevilo(p.popust_pct);
  const stopnja = p.ddv_vrsta && p.ddv_vrsta in STOPNJA_PO_VRSTI ? STOPNJA_PO_VRSTI[p.ddv_vrsta] : stevilo(p.ddv_stopnja || '22');
  const osnova = r2(kolicina * cena * (1 - popust / 100));
  const ddv = r2(osnova * stopnja / 100);
  return { osnova, ddv, skupaj: r2(osnova + ddv), stopnja };
}

export function vrstaPoStopnji(stopnja: number): string {
  if (stopnja >= 19) return 'IZD_22';
  if (stopnja >= 8) return 'IZD_95';
  if (stopnja >= 4) return 'IZD_5';
  return 'IZD_OPROSCEN';
}

/** Rekapitulacija po (vrsta, stopnja): DDV iz seštete osnove (kot backend). */
export function rekapitulacija(postavke: PostavkaVnos[]): RekapVrstica[] {
  const skupine = new Map<string, RekapVrstica>();
  for (const p of postavke) {
    const iz = izracunajPostavko(p);
    const vrsta = p.ddv_vrsta || vrstaPoStopnji(iz.stopnja);
    const k = `${vrsta}|${iz.stopnja}`;
    const g = skupine.get(k) || { ddv_vrsta: vrsta, stopnja: iz.stopnja, osnova: 0, ddv: 0 };
    g.osnova = r2(g.osnova + iz.osnova);
    skupine.set(k, g);
  }
  const out = Array.from(skupine.values()).map(g => ({ ...g, ddv: r2(g.osnova * g.stopnja / 100) }));
  out.sort((a, b) => (b.stopnja - a.stopnja) || a.ddv_vrsta.localeCompare(b.ddv_vrsta));
  return out;
}

export function vsote(rekap: RekapVrstica[]): { osnova: number; ddv: number; skupaj: number } {
  const osnova = r2(rekap.reduce((s, g) => s + g.osnova, 0));
  const ddv = r2(rekap.reduce((s, g) => s + g.ddv, 0));
  return { osnova, ddv, skupaj: r2(osnova + ddv) };
}

/** '{LL}-{NNNNN}', 2026, 7 -> '26-00007' (zrcalo stevilcenje.oblikuj_stevilko; za primer v Nastavitvah). */
export function oblikujStevilko(fmtNiz: string, leto: number, n: number, oznaka = ''): string {
  let out = (fmtNiz || '{LL}-{NNNNN}').replace('{LLLL}', String(leto)).replace('{LL}', String(leto).slice(-2)).replace('{OZN}', oznaka);
  out = out.replace(/\{N+\}/g, m => String(n).padStart(m.length - 2, '0'));
  return out.slice(0, 40);
}

export const TIP_OZNAKE: Record<string, string> = { racun: 'Račun', avans: 'Avansni račun', dobropis: 'Dobropis', ponudba: 'Ponudba', predracun: 'Predračun', narocilo: 'Naročilo kupca', dobavnica: 'Dobavnica', cmr: 'CMR' }; // [PATCH izdaja-prodajni-dok-ui v1] // [PATCH dobavnica-cmr-fe v1]
export const STATUS_OZNAKE: Record<string, string> = {
  osnutek: 'osnutek', izdan: 'izdan', poslan: 'poslan', delno_placan: 'delno plačan', placan: 'plačan', zapadel: 'zapadel',
  storniran: 'storniran', poracunan: 'poračunan', poslana: 'poslana', sprejeta: 'sprejeta', zavrnjena: 'zavrnjena', potekla: 'potekla',
  izdana: 'izdana', preklicana: 'preklicana', potekel: 'potekel', preklican: 'preklican', odprto: 'odprto', // [PATCH izdaja-prodajni-dok-ui v1]
  delno_realizirano: 'delno realizirano', realizirano: 'realizirano', preklicano: 'preklicano',
};
export const PLACILO_OZNAKE: Record<string, string> = { nakazilo: 'nakazilo na TRR', gotovina: 'gotovina', kartica: 'kartica', pobot: 'pobot' };
export const VRSTA_POSTAVKE_OZNAKE: Record<string, string> = { storitev: 'storitev', blago: 'blago', material: 'material' };

export function datumSl(iso: string | null | undefined): string {
  if (!iso) return '';
  const [l, m, d] = String(iso).slice(0, 10).split('-');
  return d && m && l ? `${d}.${m}.${l}` : String(iso);
}

export function danesIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function novKljuc(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}
